
import React, { useState, useRef } from 'react';
import { Download, X, AlertCircle, Upload, FileSpreadsheet, ChevronDown, ChevronUp, Folder } from 'lucide-react';
import { SynonymGroup } from '../types';
import { parseSynonymGroupsFromTSV } from '../utils/glossaryUtils';
import * as XLSX from 'xlsx';

interface SynonymImportModalProps {
    onClose: () => void;
    onImport: (groups: SynonymGroup[]) => void;
}

const HEADER_ALIASES: Record<keyof ColumnMap, string[]> = {
    source: ['source', 'từ gốc', 'tu goc', 'sources'],
    target: ['target', 'bản dịch', 'ban dich', 'translation', 'nghĩa đích'],
    context: ['context', 'ngữ cảnh', 'ngu canh'],
    isDefault: ['default', 'mặc định', 'mac dinh', 'is default', 'isdefault'],
    category: ['category', 'danh mục', 'danh muc', 'nhóm', 'nhom', 'folder'],
    sourceLang: ['source lang', 'sourcelang', 'ngôn ngữ nguồn', 'ngon ngu nguon', 'source language'],
    targetLang: ['target lang', 'targetlang', 'ngôn ngữ đích', 'ngon ngu dich', 'target language'],
};

interface ColumnMap {
    source: number;
    target: number;
    context: number;
    isDefault: number;
    category: number;
    sourceLang: number;
    targetLang: number;
}

function detectColumns(header: string[]): ColumnMap {
    const norm = header.map(h => String(h || '').toLowerCase().trim());
    const find = (keys: string[]) => norm.findIndex(h => keys.some(k => h === k || h.includes(k)));
    return {
        source: find(HEADER_ALIASES.source),
        target: find(HEADER_ALIASES.target),
        context: find(HEADER_ALIASES.context),
        isDefault: find(HEADER_ALIASES.isDefault),
        category: find(HEADER_ALIASES.category),
        sourceLang: find(HEADER_ALIASES.sourceLang),
        targetLang: find(HEADER_ALIASES.targetLang),
    };
}

const SynonymImportModal: React.FC<SynonymImportModalProps> = ({ onClose, onImport }) => {
    const [previewGroups, setPreviewGroups] = useState<SynonymGroup[]>([]);
    const [fileName, setFileName] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [showPaste, setShowPaste] = useState(false);
    const [pasteText, setPasteText] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const parseRows = (rows: string[][]) => {
        if (rows.length === 0) {
            setError('File rỗng.');
            return;
        }

        // Detect header
        const firstRow = rows[0] || [];
        const detected = detectColumns(firstRow);
        const hasHeader = detected.source >= 0 && detected.target >= 0;

        let cols: ColumnMap;
        let dataRows: string[][];
        if (hasHeader) {
            cols = detected;
            dataRows = rows.slice(1);
        } else {
            // Fallback fixed positions: A=Source, B=Target, C=Context, D=Default, E=Category, F=SourceLang, G=TargetLang
            cols = { source: 0, target: 1, context: 2, isDefault: 3, category: 4, sourceLang: 5, targetLang: 6 };
            dataRows = rows;
        }

        // Build canonical TSV: source\ttarget\tcontext\tisDefault\tcategory\tsourceLang\ttargetLang
        const tsvLines = dataRows.map(row => {
            const get = (idx: number) => (idx >= 0 ? String(row[idx] ?? '').trim() : '');
            const source = get(cols.source);
            const target = get(cols.target);
            if (!source || !target) return null;
            return [
                source,
                target,
                get(cols.context),
                get(cols.isDefault),
                get(cols.category),
                get(cols.sourceLang),
                get(cols.targetLang),
            ].join('\t');
        }).filter(Boolean) as string[];

        if (tsvLines.length === 0) {
            setError('Không tìm thấy dòng dữ liệu hợp lệ. Cần có cột Từ gốc và Bản dịch.');
            setPreviewGroups([]);
            return;
        }

        const groups = parseSynonymGroupsFromTSV(tsvLines.join('\n'));
        setPreviewGroups(groups);
        setError('');
    };

    const handleFileUpload = async (file: File) => {
        try {
            setError('');
            setFileName(file.name);
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as string[][];
            parseRows(jsonData);
        } catch (err) {
            console.error('Failed to read Excel file', err);
            setError('Không đọc được file. Chỉ chấp nhận .xlsx hợp lệ.');
            setPreviewGroups([]);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileUpload(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileUpload(file);
    };

    const handleParsePaste = () => {
        if (!pasteText.trim()) return;
        // Split into rows by newline, then by tab
        const rows = pasteText.split(/\r?\n/).filter(l => l.trim()).map(l => l.split('\t'));
        setFileName('(dán từ clipboard)');
        parseRows(rows);
    };

    const handleImport = () => {
        if (previewGroups.length === 0) return;
        onImport(previewGroups);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-[820px] max-w-full h-[640px] max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                        <Download className="w-5 h-5 text-indigo-600" />
                        Nhập từ điển thuật ngữ
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-hidden flex flex-col bg-slate-50">
                    {/* Upload zone */}
                    <div className="px-6 pt-5 pb-3">
                        {previewGroups.length === 0 && !error ? (
                            <label
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                                onDrop={handleDrop}
                                className={`block border-2 border-dashed rounded-xl px-6 py-8 text-center cursor-pointer transition-colors ${
                                    isDragging
                                        ? 'border-indigo-400 bg-indigo-50'
                                        : 'border-slate-300 bg-white hover:border-indigo-300 hover:bg-indigo-50/40'
                                }`}
                            >
                                <FileSpreadsheet className="w-10 h-10 text-indigo-500 mx-auto mb-2" />
                                <div className="text-sm font-bold text-slate-800 mb-1">
                                    Tải file Excel (.xlsx) hoặc kéo thả vào đây
                                </div>
                                <div className="text-xs text-slate-500 mb-3">
                                    Cột nhận dạng tự động: Từ gốc · Bản dịch · Ngữ cảnh · Mặc định · Danh mục · Ngôn ngữ nguồn · Ngôn ngữ đích
                                </div>
                                <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700">
                                    <Upload className="w-4 h-4" />
                                    Chọn file
                                </span>
                                <input
                                    type="file"
                                    accept=".xlsx, .xls"
                                    ref={fileInputRef}
                                    onChange={handleFileInput}
                                    className="hidden"
                                />
                            </label>
                        ) : (
                            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
                                <FileSpreadsheet className="w-5 h-5 text-emerald-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-slate-800 truncate">{fileName}</div>
                                    <div className="text-xs text-slate-500">
                                        {error
                                            ? <span className="text-red-600">{error}</span>
                                            : `Phân tích: ${previewGroups.length} thuật ngữ sẵn sàng nhập`
                                        }
                                    </div>
                                </div>
                                <button
                                    onClick={() => { setPreviewGroups([]); setFileName(''); setError(''); }}
                                    className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
                                >
                                    Chọn lại
                                </button>
                            </div>
                        )}

                        {/* Paste fallback (collapsed by default) */}
                        <button
                            onClick={() => setShowPaste(s => !s)}
                            className="mt-3 text-xs font-medium text-slate-500 hover:text-slate-700 flex items-center gap-1"
                        >
                            {showPaste ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            Hoặc dán dữ liệu từ clipboard (TSV)
                        </button>
                        {showPaste && (
                            <div className="mt-2">
                                <textarea
                                    value={pasteText}
                                    onChange={(e) => setPasteText(e.target.value)}
                                    placeholder="HDQT, BOD&#9;Board of Directors&#9;formal&#9;yes&#9;Tổ chức"
                                    rows={4}
                                    className="w-full p-3 border border-slate-300 rounded-lg font-mono text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
                                    spellCheck={false}
                                />
                                <button
                                    onClick={handleParsePaste}
                                    disabled={!pasteText.trim()}
                                    className="mt-2 px-3 py-1.5 bg-slate-700 text-white rounded-md text-xs font-bold hover:bg-slate-800 disabled:opacity-50"
                                >
                                    Phân tích
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Preview */}
                    <div className="flex-1 overflow-auto px-6 pb-4">
                        {previewGroups.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400">
                                <AlertCircle className="w-10 h-10 mb-3 opacity-30" />
                                <p className="text-sm">Chưa có dữ liệu — chọn file để xem trước.</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Xem trước ({previewGroups.length} thuật ngữ)
                                </div>
                                {previewGroups.map((group, i) => (
                                    <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap gap-1 mb-1">
                                                {group.sources.map((s, idx) => (
                                                    <span key={idx} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs border border-indigo-100">{s}</span>
                                                ))}
                                            </div>
                                            <div className="text-sm text-slate-700 space-y-0.5">
                                                {group.targets.map((t, idx) => (
                                                    <div key={idx} className="flex items-center gap-2">
                                                        <span className={t.isDefault ? 'font-bold text-emerald-700' : ''}>{t.text}</span>
                                                        {t.context && <span className="text-slate-400 italic text-xs">({t.context})</span>}
                                                        {t.isDefault && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 rounded font-bold">MẶC ĐỊNH</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        {group.category && (
                                            <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs border border-slate-200">
                                                <Folder className="w-3 h-3" />
                                                {group.category}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-4 border-t border-slate-200 bg-white flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium text-sm transition-colors"
                    >
                        Hủy
                    </button>
                    <button
                        onClick={handleImport}
                        disabled={previewGroups.length === 0}
                        className="px-6 py-2 bg-emerald-500 text-white rounded-lg font-bold text-sm hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-200"
                    >
                        Nhập {previewGroups.length > 0 ? `${previewGroups.length} thuật ngữ` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SynonymImportModal;
